import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import { useDebounce, useScrollHorizontallyToElement } from "../../../../../../../hooks";
import { idUtils } from "../../../../../../../utils";

import { FlexContainers, PageSelector } from "../../../../../../../components";
import { ListWithIconsForm } from "./ListWithIconsForm";
import { ListWithIconsWrapper } from "./styled";

export const ListWithIcons = ({
  language,
  configList,
  iconsData,
  thumbnailsData,
  configErrors,
  showErrors,
  languageListLength,
  onChange,
  onChangeIconsData,
  onChangeThumbnailsData,
  pushErrorLang,
  removeErrorLang
}) => {
  const [sections, setSections] = useState([]);
  const [activeSection, setActiveSection] = useState();

  const [configs, setConfigs] = useState([]);
  const activeConfig = useMemo(() => {
    return configs?.find((item) => item.id === activeSection);
  }, [configs, activeSection]);

  const activeConfigErrors = useMemo(() => {
    const errors = configErrors?.config?.find((item) => item.id === activeConfig?.id);
    return errors;
  }, [activeConfig, configErrors]);

  const iconData = useMemo(() => {
    return iconsData?.find((el) => el.id === activeSection);
  }, [iconsData, activeSection]);

  const thumbnailData = useMemo(() => {
    return thumbnailsData?.find((el) => el.id === activeSection);
  }, [thumbnailsData, activeSection]);

  const getActiveSectionElement = useCallback((activeSectionId) => {
    const el = document.getElementById(activeSectionId);
    return el;
  }, []);

  useScrollHorizontallyToElement(activeSection, getActiveSectionElement);

  const errorPages = useMemo(() => {
    const errors = configErrors?.config?.map((item) => item?.id) ?? [];

    return errors;
  }, [configErrors]);

  useEffect(() => {
    if (configList?.length) {
      setSections(
        configList.map((config, index) => {
          return { id: config.id, title: index + 1 };
        })
      );
      setConfigs(configList);
      setTimeout(() => {
        setActiveSection(configList[0]?.id);
      });
    } else {
      const firstSectionId = idUtils.getUniqueId();

      setSections([{ id: firstSectionId, title: 1 }]);
      setConfigs([{ id: firstSectionId, icon: null, title: "", description: "" }]);

      setTimeout(() => {
        setActiveSection(firstSectionId);
      });
    }
  }, [configList]);

  useEffect(() => {
    return () => {
      pushErrorLang(language);
    };
  }, [language]);

  useEffect(() => {
    if (!errorPages?.length && !configErrors?.summary) {
      removeErrorLang(language);
    }
  }, [language, errorPages]);

  const onChangeConfigList = useCallback(
    (updatedConfig) => {
      const updatedConfigIdx = configs?.findIndex((item) => item.id === updatedConfig?.id);

      if (updatedConfigIdx !== -1) {
        const newConfigList = [
          ...configs.slice(0, updatedConfigIdx),
          updatedConfig,
          ...configs.slice(updatedConfigIdx + 1)
        ];

        setConfigs(newConfigList);
      }
    },
    [configs, configList]
  );

  const onAddNewSection = () => {
    const newId = idUtils.getUniqueId();
    const newTitle = sections.length + 1;

    setSections((prev) => [...prev, { id: newId, title: newTitle }]);
    setConfigs((prev) => [...prev, { id: newId, icon: null, title: "", description: "" }]);
    setActiveSection(newId);
  };

  const onRemoveSection = (id) => {
    let newSections = sections.filter((section) => section.id !== id);
    let removeSectionIdx = sections.findIndex((section) => section.id === id);
    let prevSectionId = sections[removeSectionIdx - 1]?.id;
    let firstSectionId = id;

    if (newSections?.length === 0) {
      newSections = sections;
      setConfigs([{ id: firstSectionId }]);
    } else {
      if (prevSectionId) {
        setActiveSection(prevSectionId);
      } else {
        firstSectionId = newSections[0]?.id;
        setActiveSection(firstSectionId);
      }

      setConfigs((prev) => prev.filter((config) => config.id !== id));
    }

    newSections = newSections.map((section, index) => {
      return { ...section, title: index + 1 };
    });

    setSections(newSections);
  };

  const onChangeSectionsOrder = (newSections) => {
    const updatedSections = newSections.map((section, index) => {
      return { ...section, title: index + 1 };
    });

    const updatedConfigs = [];

    updatedSections.forEach((section) => {
      const config = configs.find((item) => item.id === section.id);
      updatedConfigs.push(config);
    });

    setSections(updatedSections);
    setConfigs(updatedConfigs);
  };

  useDebounce(onChange, 100, configs, languageListLength);

  return (
    <ListWithIconsWrapper Container={FlexContainers.FlexColumnStart} gap={16} width={"100%"}>
      <PageSelector
        isReordable
        pages={sections}
        setPages={onChangeSectionsOrder}
        activePage={activeSection}
        errorPages={showErrors ? errorPages : []}
        setActivePage={setActiveSection}
        onAddNewPage={onAddNewSection}
      />

      {activeConfig && (
        <ListWithIconsForm
          config={activeConfig}
          iconData={iconData}
          thumbnailData={thumbnailData}
          configErrors={activeConfigErrors}
          showErrors={showErrors}
          onChange={onChangeConfigList}
          onChangeIconsData={onChangeIconsData}
          onChangeThumbnailsData={onChangeThumbnailsData}
          onRemove={onRemoveSection}
        />
      )}
    </ListWithIconsWrapper>
  );
};

ListWithIcons.propTypes = {
  language: PropTypes.string.isRequired,
  configList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      icon: PropTypes.object.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired
    })
  ),
  iconsData: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number.isRequired, icon: PropTypes.object })
  ).isRequired,
  thumbnailsData: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number.isRequired, media: PropTypes.object })
  ).isRequired,
  configErrors: PropTypes.shape({
    language: PropTypes.string.isRequired,
    summary: PropTypes.bool,
    config: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        icon: PropTypes.bool.isRequired,
        title: PropTypes.bool.isRequired,
        description: PropTypes.bool.isRequired
      })
    )
  }),
  showErrors: PropTypes.bool.isRequired,
  languageListLength: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onChangeIconsData: PropTypes.func.isRequired,
  onChangeThumbnailsData: PropTypes.func.isRequired,
  pushErrorLang: PropTypes.func.isRequired,
  removeErrorLang: PropTypes.func.isRequired
};
